/**
 * Pure rules of the Clients bulk actions while the Lead Pipeline is on (LP6; story §54-§60, QA-5, D7, D13; TCS §4.10 U-BLK-30…39):
 * the one impact preview of a bulk Set stage / Set client type, the §60 summary, the people delete's impact line and which client
 * folders a delete may remove. No Angular, no I/O: ClientBulkActionsComponent and ContactListComponent apply them. The copy is the
 * TCS's proposed copy (BLK·OQ-1, adopted), in the tenant's label where it names a case (A3-10).
 */

/** What the bulk bar does with one Set stage / Set client type (OQ-2, OQ-3; U-BLK-38). */
export type LeadBulkRoute = 'single' | 'preview' | 'direct';

/** The impact preview, as the app's small confirm draws it (heading, lines, Continue). */
export interface LeadBulkDialog {
    /** The heading WITHOUT its "?" (the confirm adds it). */
    readonly title: string;
    readonly lines: readonly string[];
}

/** The preview counts the server answers (LeadBulkStagePreviewDto). */
export interface LeadBulkStageCounts {
    readonly stageName: string | null;
    readonly stageCategory: 'open' | 'won' | 'lost' | 'none' | string;
    readonly leads: number;
    readonly rescheduled: number;
    readonly reopened: number;
    readonly customReplaced: number;
    readonly tasksCompleted: number;
    readonly cyclesCompleted: number;
    readonly caseReminders: number;
    readonly leadsRemoved: number;
}

export interface LeadBulkTypeCounts {
    readonly typeName: string | null;
    readonly leavesWorkflow: number;
    readonly startsCycle: number;
}

/** One skipped / failed person of a bulk answer (ClientBulkRecordDto). */
export interface LeadBulkRecord {
    readonly contactId: number;
    readonly name?: string | null;
    readonly reason?: string | null;
    readonly message?: string | null;
}

export interface LeadBulkSummaryToast {
    readonly level: 'success' | 'warning' | 'error';
    /** Toastr HTML (every name and server sentence escaped). */
    readonly html: string;
}

/** The Details list stops after this many names (as MD's bulk-complete summary). */
export const LEAD_BULK_DETAILS_MAX = 5;

function plural(n: number, one: string, many: string): string {
    return `${n} ${n === 1 ? one : many}`;
}

/** OQ-3 / U-BLK-38: one selected person runs the single-record flows (the Won dialog, §43, §44); more go through the preview. */
export function bulkRoute(selectedCount: number): LeadBulkRoute {
    return selectedCount === 1 ? 'single' : 'preview';
}

/**
 * §56 / QA-5 / §43 (U-BLK-30…33): the bulk Set stage preview, or null when nothing needs one - bulk Lost runs directly (QA-5) and
 * a selection where no Lead's lifecycle changes runs with no dialog (OQ-2). Zero-count lines are left out.
 */
export function stagePreviewDialog(c: LeadBulkStageCounts, caseLabel: string): LeadBulkDialog | null {
    const stage = c.stageName || 'this stage';
    if (c.stageCategory === 'lost' || c.leads <= 0) {
        return null;
    }
    if (c.stageCategory === 'none') {
        const n = c.leadsRemoved;
        if (n <= 0) {
            return null;
        }
        return {
            title: `Remove ${plural(n, 'Lead', 'Leads')} from Pipeline`,
            lines: [n === 1
                ? 'Clearing the Stage will remove this Lead from the Pipeline and end its Primary Lead follow-up task.'
                : 'Clearing the Stage will remove these Leads from the Pipeline and end their Primary Lead follow-up tasks.'],
        };
    }
    const title = `Move ${plural(c.leads, 'Lead', 'Leads')} to ${stage}`;
    if (c.stageCategory === 'won') {
        const lines: string[] = [];
        if (c.cyclesCompleted > 0) {
            lines.push(`${plural(c.cyclesCompleted, 'Lead cycle', 'Lead cycles')} will be completed`);
        }
        if (c.caseReminders > 0) {
            const label = (caseLabel || 'Case').trim();
            lines.push(`${plural(c.caseReminders, `${label}-creation reminder`, `${label}-creation reminders`)} will be created for 3 days from now`);
        }
        return { title, lines };
    }
    const lines: string[] = [`${plural(c.leads, 'Stage', 'Stages')} will change`];
    if (c.rescheduled > 0) {
        lines.push(`${plural(c.rescheduled, 'follow-up', 'follow-ups')} will be rescheduled`);
    }
    if (c.reopened > 0) {
        lines.push(`${plural(c.reopened, 'completed Lead task', 'completed Lead tasks')} will reopen`);
    }
    if (c.customReplaced > 0) {
        lines.push(`${plural(c.customReplaced, 'custom date', 'custom dates')} will be replaced`);
    }
    if (c.tasksCompleted > 0) {
        lines.push(`${plural(c.tasksCompleted, 'Lead task', 'Lead tasks')} will be completed`);
    }
    return { title, lines };
}

/** §55 / §44 bulk form (BLK-14, BLK-16): the bulk Set client type preview, or null for a type-only change (no dialog). */
export function typePreviewDialog(c: LeadBulkTypeCounts): LeadBulkDialog | null {
    if (c.leavesWorkflow > 0) {
        return {
            title: `Remove ${plural(c.leavesWorkflow, 'Lead', 'Leads')} from Lead workflow`,
            lines: [`Changing the Client Type will remove the Stage and end the Primary Lead follow-up task for ${plural(c.leavesWorkflow, 'Lead', 'Leads')}.`],
        };
    }
    if (c.startsCycle > 0) {
        return {
            title: `Change ${plural(c.startsCycle, 'client', 'clients')} to ${c.typeName || 'this type'}`,
            lines: [`${plural(c.startsCycle, 'Lead cycle', 'Lead cycles')} will start`],
        };
    }
    return null;
}

/** §57 / D7 (U-BLK-39): the people delete confirm's impact line; null when no active Lead is selected. */
export function deleteImpactLine(activeLeads: number): string | null {
    if (!activeLeads || activeLeads <= 0) {
        return null;
    }
    return activeLeads === 1
        ? '1 is an active Lead: its Lead cycle will end and its Primary Lead task will be completed.'
        : `${activeLeads} are active Leads: their Lead cycles will end and their Primary Lead tasks will be completed.`;
}

/** A lead left out in the Mark-lost picker's One by one ("Skip this lead", BML-002): never sent, counted as skipped. */
export const LEAD_BULK_SKIPPED_BY_USER = 'skipped_by_user';

/** A skip reason in words (§60 Details). */
export function skipReasonText(reason: string | null | undefined, targetName: string | null): string {
    switch (reason) {
        case LEAD_BULK_SKIPPED_BY_USER:
            return 'skipped';
        case 'already_in_stage':
            return targetName ? `already in ${targetName}` : 'already in that stage';
        case 'no_stage':
            return 'no stage to clear';
        case 'already_this_type':
            return targetName ? `already ${targetName}` : 'already that type';
        case 'already_deleted':
            return 'already deleted';
        default:
            return 'no longer exists';
    }
}

/** HTML-escapes server and user text for the toastr summary (enableHtml). */
export function escapeSummaryText(value: string | null | undefined): string {
    return (value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function detailLines(skipped: readonly LeadBulkRecord[], failed: readonly LeadBulkRecord[], targetName: string | null): string[] {
    const all = [
        ...skipped.map((r) => `${escapeSummaryText(r.name || 'A client')} — ${escapeSummaryText(skipReasonText(r.reason, targetName))}`),
        ...failed.map((r) => `${escapeSummaryText(r.name || 'A client')} — ${escapeSummaryText(r.message || 'failed')}`),
    ];
    if (all.length <= LEAD_BULK_DETAILS_MAX) {
        return all;
    }
    return [...all.slice(0, LEAD_BULK_DETAILS_MAX), `+${all.length - LEAD_BULK_DETAILS_MAX} more`];
}

function levelOf(done: number, failed: number): 'success' | 'warning' | 'error' {
    if (failed > 0) {
        return done > 0 ? 'warning' : 'error';
    }
    return 'success';
}

/** §60 / QA-9 (U-BLK-34): `Updated: X · Skipped: Y · Failed: Z`, then each skipped / failed name with its reason. */
export function bulkSummary(updated: number, skipped: readonly LeadBulkRecord[], failed: readonly LeadBulkRecord[], targetName: string | null): LeadBulkSummaryToast {
    const head = `<b>Updated: ${updated}</b> · Skipped: ${skipped.length} · Failed: ${failed.length}`;
    const details = detailLines(skipped, failed, targetName);
    return { level: levelOf(updated, failed.length), html: [head, ...details].join('<br>') };
}

/** §57 / D7 (U-BLK-35): `16 deleted · 2 skipped · 1 failed` (zero parts left out), then the Details. */
export function deleteSummary(deleted: number, skipped: readonly LeadBulkRecord[], failed: readonly LeadBulkRecord[]): LeadBulkSummaryToast {
    const parts = [`<b>${deleted} deleted</b>`];
    if (skipped.length > 0) {
        parts.push(`${skipped.length} skipped`);
    }
    if (failed.length > 0) {
        parts.push(`${failed.length} failed`);
    }
    const details = detailLines(skipped, failed, null);
    return { level: levelOf(deleted, failed.length), html: [parts.join(' · '), ...details].join('<br>') };
}

/**
 * D7 (U-BLK-37): the client folders a people delete may remove - only when the box is ticked, only for the people the server says
 * it deleted, only after it answered (an error = none: files kept rather than lost).
 */
export function foldersToDelete(boxTicked: boolean, isError: boolean, deletedIds: readonly number[] | null | undefined): number[] {
    if (!boxTicked || isError) {
        return [];
    }
    return (deletedIds || []).filter((id) => id > 0);
}

/** D7 (U-BLK-37, BLK-21): the "Delete client folder as well" box's default - off for people while the Lead Pipeline is on. */
export function peopleFolderBoxDefault(leadPipelineOn: boolean): boolean {
    return !leadPipelineOn;
}
